import { attr, belongsTo } from "@ember-data/model";
import type Store from "@ember-data/store";
import { service } from "@ember/service";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/abstract/planified-journal-event";
import type Patient from "parog-web/models/patient";
import type TaskProcessedJournalEvent from "parog-web/models/task-processed-journal-event";
import type ParogService from "parog-web/services/parog";
import tWithDefault from "parog-web/utils/t-with-default";
import type { BaseTaskPlanned } from "./recursive-task-planned-journal-event";
import type TreatmentHistoryChangedJournalEvent from "./treatment-history-changed-journal-event";

export enum TaskType {
  vitalSignsMeasurement = "vital-signs-measurement",
  compoundSurvey = "compound-survey",
  updatePatientBirthdayReminder = "update-patient-birthday-reminder",
  patientInfo = "patient-info"
}

//---- Automatically generated, don't update this part ----

type ResponsibleTypeStringEnum = "medic" | "patient";

/**
 * Represents a task that has been planified.
 * A task is usually something to do in the application.
 */
export default class TaskPlannedJournalEvent
  extends JournalEvent
  implements BaseTaskPlanned
{
  static override type = "task-planned-journal-event";

  /**
   * The type of the responsible. Could be null.
   */
  @attr("string") declare responsibleType: ResponsibleTypeStringEnum;

  /**
   * The id of the responsible, could be null
   */
  @attr("number") declare responsibleId: number;

  /**
   * An id describing the nature of the task.
   */
  @attr("string") declare taskType: string;

  /**
   * An identifier to be able to link the task to a JournalEvent field (mainly used by TreatmentHistoryJournalEvent)
   */
  @attr("string") declare originTaskIdentifier: string;

  /**
   * A boolean to remind the responsible to do the task
   */
  @attr("boolean", {
    allowNull: true
  })
  declare remindResponsible: boolean;

  /**
   * A JournalEvent type that will process the task automatically after a JE creation. Could be a CompoundSurvey's id if the taskType is "compound-survey".
   * This should be written in snake case.
   * Example: 'weight-measured-journal-event'
   */
  @attr("string") declare processingJournalEventType: string;

  /**
   * Date of the last HPRIM
   */
  @attr("date") declare lastHprimDate: Date;

  /**
   * A link to be redirected when click on the task
   */
  @attr("string") declare link: string;

  /**
   * Models to pass to LinkTo component
   */
  @attr("string") declare models: string;

  @belongsTo("task-processed-journal-event", {
    async: false,
    inverse: "taskPlannedJournalEvent"
  })
  declare taskProcessedJournalEvent: TaskProcessedJournalEvent;

  static override validations(intl: IntlService) {
    return {
      taskType: validatePresence({
        presence: true,
        description: intl.t("validations.task-planned-journal-event.task-type")
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.task-planned-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ],
      responsibleId: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.task-planned-journal-event.responsible-id"
        )
      }),
      responsibleType: validateInclusion({
        list: ["medic", "patient"],
        allowBlank: true,
        description: intl.t(
          "validations.task-planned-journal-event.responsible-type"
        )
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "taskPlannedJournalEvents"
  })
  declare patient: Patient;

  @belongsTo("treatment-history-changed-journal-event", {
    async: false,
    inverse: "taskPlannedJournalEvents"
  })
  declare treatmentHistoryChangedJournalEvent: TreatmentHistoryChangedJournalEvent;

  @service declare store: Store;
  @service declare parog: ParogService;

  get isVitalSignMeasurement(): boolean {
    return this.taskType == TaskType.vitalSignsMeasurement;
  }

  get isCompoundSurvey(): boolean {
    return this.taskType == TaskType.compoundSurvey;
  }

  get isPatientInfo(): boolean {
    return this.taskType == TaskType.patientInfo;
  }

  get kindOfLink(): "internal" | "external" | undefined {
    return this.link
      ? this.link.startsWith("http")
        ? "external"
        : "internal"
      : undefined;
  }

  override get hideOnTimeline(): boolean {
    return this.isProcessed;
  }

  get hasAvailablePatientType(): boolean {
    return (Object.values(TaskType) as string[]).includes(this.taskType);
  }

  async processTask(
    actionTaken: string,
    processOlderTasks = false
  ): Promise<TaskProcessedJournalEvent> {
    if (this.isProcessed) {
      throw `The processed task '${this.id}' can't be processed again.`;
    }

    const taskProcessedJournalEvent = await this.store
      .createRecord("task-processed-journal-event", {
        patient: this.patient,
        occurredAt: new Date(),
        taskPlannedJournalEvent: this,
        actionTaken,
        processOlderTasks
      })
      .save();

    return taskProcessedJournalEvent;
  }

  /**
   * Shift the JE for the given amount of time.
   * Will archive this one and create a new one with a new 'occurredAt'.
   * @param number - The number of unit we want to shift the date of
   * @param periodStr - A string representing a unit of measurement for 'dayjs'.
   * Like 'days', 'months', 'years' ...
   */
  async shift(number: number, periodStr: dayjs.ManipulateType) {
    const hoursJE = dayjs(this.occurredAt).get("hour");
    const minutesJE = dayjs(this.occurredAt).get("minute");
    return await this.shiftAt(
      dayjs()
        .add(number, periodStr)
        .set("hour", hoursJE)
        .set("minute", minutesJE)
        .toDate()
    );
  }

  /**
   * Archive this JE and create a new one at the new 'occurredAt'
   * @param occurredAt
   */
  async shiftAt(occurredAt: Date): Promise<TaskPlannedJournalEvent> {
    if (this.isProcessed) {
      throw `The processed task '${this.id}' can't be shifted.`;
    }

    this.occurredAt = occurredAt;
    return this.save();
  }

  get isProcessed() {
    return Boolean(this.taskProcessedJournalEvent);
  }

  get icon(): string {
    return "bio";
  }

  get taskLabel(): string {
    return this.intl.t(`task.types.${this.taskType}`);
  }
}
// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "task-planned-journal-event": TaskPlannedJournalEvent;
  }
}
